import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { ThemeProvider } from 'styled-components';
import { isEmailVerifiedSignalEnabled } from '@helpcenter/feature-flags';
import { lightenColor, readableTextColor } from '@helpcenter/styles/src/colors';
import { GlobalStyled } from '@helpcenter/styles/src/global';
import { createMemoryHistory } from 'history';
import { di } from 'react-magnetic-di';
import type { BrowserHistory, Route } from 'react-resource-router';
import { RouteComponent, Router } from 'react-resource-router';
import { getViewHomePageRoute, getEditHomePageRoute } from 'routes/advanced-help-center';
import { aiAnswerRoute } from 'routes/aianswer';
import { approvalsRoute } from 'routes/approvals';
import { consentFormRoute } from 'routes/consent-form';
import {
	getHelpCenterConversationPageRoute,
	viewPortalConversationPageRoute,
} from 'routes/conversation';
import { getEditCustomPageRoute, getViewCustomPageRoute } from 'routes/custom-pages';
import { editTopicRoute, addTopicRoute } from 'routes/edit-topic';
import {
	embedSinglePortalRoute,
	embedKBArticleRoute,
	embedFeaturedPortalsRoute,
	getEmbedCreateRequestRoute,
	embedLoginSuccessRoute,
} from 'routes/embed-smartlinks';
import { getHelpCenterRoute } from 'routes/help-center';
import {
	viewAllSearchResultsRoute,
	viewPortalAllSearchResultsRoute,
	viewArticleRoute,
	viewCrossSiteArticleRoute,
	viewKbPageRoute,
	viewPortalArticleRoute,
	viewCrossSitePortalArticleRoute,
	viewPortalCategoryArticleRoute,
	viewPortalKnowledgeCategoryRoute,
} from 'routes/kb';
import { loginRoute, portalLoginRoute } from 'routes/login';
import { portalAccessForbiddenRoute } from 'routes/portal-access-forbidden';
import { getPortalHomeRoutes } from 'routes/portal-home';
import { requestApprovalRoute } from 'routes/request-approval';
import { requestDetailsRoute } from 'routes/request-details';
import { requestsListRoute } from 'routes/requests';

import {
	portalInvitedSignupRoute,
	portalCompleteSignupRoute,
	portalResetPasswordRoute,
	portalSignupRoute,
	userCheckMailRoute,
	userCompleteSignupRoute,
	userResetPasswordRoute,
	userSignupRoute,
} from 'routes/signup';
import { getSurveyRoute } from 'routes/surveys';
import { getViewTopicRoute } from 'routes/topics';
import { userVerifyEmailRoute } from 'routes/verify-email';
import { getHeadless } from 'state/selectors/env';
import { getHelpCenterTitleColor, getThemeColor } from 'state/selectors/help-center';
import { UFOWatcher } from 'utils/ufo-watcher';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	isFlexiHCEnabledInDefaultHC,
	isAdvancedHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center';
import { getIsHCCustomizationEnabled } from '@atlassian/help-center-common-util/env';
import { history, getBaseName } from '@atlassian/help-center-common-util/history';
import { resourcesPlugin } from '../resource-plugin';
import {
	requestFeedbackRoute,
	unsubscribeRoute,
	userProfileRoute,
	anonymousRequestSuccessRoute,
	getRequestCreateRoute,
	portalReorderRoute,
	notFoundErrorRoute,
} from '../routes';

export const getRoutes = (): Route[] => {
	return [
		...(isAdvancedHelpCenter() || isFlexiHCEnabledInDefaultHC()
			? [getViewHomePageRoute(), getEditHomePageRoute()]
			: []),
		...(isEmailVerifiedSignalEnabled() ? [userVerifyEmailRoute] : []),
		viewPortalConversationPageRoute,
		aiAnswerRoute,
		consentFormRoute,
		loginRoute,
		userResetPasswordRoute,
		userSignupRoute,
		userCompleteSignupRoute,
		userCheckMailRoute,
		portalAccessForbiddenRoute,
		portalLoginRoute,
		portalSignupRoute,
		portalResetPasswordRoute,
		portalCompleteSignupRoute,
		portalInvitedSignupRoute,
		requestFeedbackRoute,
		unsubscribeRoute,
		getHelpCenterRoute(),
		getEditHomePageRoute('edit-homepage', '/edit'),
		requestDetailsRoute,
		viewKbPageRoute,
		viewAllSearchResultsRoute,
		viewPortalAllSearchResultsRoute,
		viewPortalCategoryArticleRoute,
		viewPortalArticleRoute,
		viewCrossSitePortalArticleRoute,
		viewArticleRoute,
		viewCrossSiteArticleRoute,
		requestsListRoute,
		approvalsRoute,
		requestApprovalRoute,
		userProfileRoute,
		viewPortalKnowledgeCategoryRoute,
		anonymousRequestSuccessRoute,
		getRequestCreateRoute(),
		getSurveyRoute(),
		getPortalHomeRoutes(),
		getEmbedCreateRequestRoute(),
		embedSinglePortalRoute,
		embedKBArticleRoute,
		...(FeatureGates.checkGate('embed_hc_featured_portals') ? [embedFeaturedPortalsRoute] : []),
		...(FeatureGates.checkGate('jsm_portal_rovo_integration') ? [embedLoginSuccessRoute] : []),
		portalReorderRoute(true),
		editTopicRoute,
		addTopicRoute,
		getViewTopicRoute(),
		...getHelpCenterConversationPageRoute(),
		...(getIsHCCustomizationEnabled() ? [getEditCustomPageRoute(), getViewCustomPageRoute()] : []),
		notFoundErrorRoute,
	];
};

export interface Props {
	headerless: boolean;
	themeColor: string;
	titleColor: string;
}

export const JsmResourceRouter = (props: Props) => {
	di(Router);

	const { themeColor, titleColor } = props;
	const readableTextColorFromTheme = readableTextColor(themeColor);
	const lightColorFromTheme = lightenColor(themeColor);
	const routes = useMemo(() => getRoutes(), []);
	const location = globalThis.location.pathname + globalThis.location.search;
	const theme = {
		titleColor,
		readableTextColor: readableTextColorFromTheme,
		color: themeColor,
		lightColor: lightColorFromTheme,
	};

	if (__SERVER__) {
		return (
			<ThemeProvider theme={theme}>
				<Router
					routes={routes}
					isGlobal={false}
					basePath={getBaseName()}
					history={createMemoryHistory({ initialEntries: [location] })}
					plugins={[resourcesPlugin]}
				>
					<GlobalStyled />
					<RouteComponent />
				</Router>
			</ThemeProvider>
		);
	}

	//isGlobal=false, to avoid the router conflicting with the sidebar's router.
	return (
		<ThemeProvider theme={theme}>
			<Router
				routes={routes}
				history={history as BrowserHistory}
				isGlobal={false}
				plugins={[resourcesPlugin]}
			>
				<GlobalStyled />
				<UFOWatcher />
				<RouteComponent />
			</Router>
		</ThemeProvider>
	);
};

const selector = createSelector(
	getHeadless,
	getThemeColor,
	getHelpCenterTitleColor,
	(headerless, themeColor, titleColor) => ({
		headerless,
		themeColor,
		titleColor,
	}),
);

export default connect(selector)(JsmResourceRouter);
